import OlMap from 'ol/Map';
import View from 'ol/View';

import Cutout from "./Cutout";
import Coordinate from "../Coordinates/Coordinate";
import OLConvertibleCoordinate from "../Coordinates/OLConvertibleCoordinate";
import WGS84, {WGS84System} from "../Coordinates/WGS84";
import {fromLonLat, toLonLat} from "ol/proj";
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import * as olExtent from 'ol/extent';
import {containsCoordinate, Extent, getCenter} from 'ol/extent';
import {DragPan} from "ol/interaction";
import {altKeyOnly, platformModifierKeyOnly} from "ol/events/condition";
import Feature from "ol/Feature";
import {Layer} from "ol/layer";
import Route from "./Route";
import {Circle, Fill, Icon, Stroke, Style} from "ol/style";
import mapMarker from "../img/marker.svg";
import locationCircle from "../img/location-circle.svg";
import locationCross from "../img/location-cross.svg";
import locationTriangle from "../img/location-triangle.svg";
import {Size} from "ol/size";
import UserInterface from "./UserInterface";
import Location from "./Location";
import UserError from "../Util/UserError";
import CoordinateConverter from "../Util/CoordinateConverter";
import Container, {ElevationMode} from "./Container";
import OlTileLoader from "../Util/OlTileLoader";
import {isProxy, toRaw} from "vue";
import {Attribution, Control} from "ol/control";
import {defaults as defaultControls} from 'ol/control/defaults.js';
import {Collection as olCollection, Geolocation} from "ol";
import {Point, Polygon} from "ol/geom";
import CircleStyle from "ol/style/Circle";
import {isIOS, isMobile} from "../Util/functions";
import debounce from "lodash.debounce";
import ElevationProvider from "./ElevationProvider.js";
import LayerGroup from "ol/layer/Group.js";
import Map3d from "./Map3d.js";
import MapImageProvider from "../Projection/MapImageProvider.js";
import Shape from "./Shape.js";

export enum MarkerType {
    MapMarker = "map_marker",
    LocationCircle = "location_circle",
    LocationCross = "location_cross",
    LocationTriangle = "location_triangle",
}

export function getMarkerImage(markerType: MarkerType): string {
    if (markerType === MarkerType.LocationCircle) {
        return locationCircle;
    } else if (markerType === MarkerType.LocationCross) {
        return locationCross;
    } else if (markerType === MarkerType.LocationTriangle) {
        return locationTriangle;
    } else {
        return mapMarker;
    }
}

export function getMarkerTextOffset(markerType: MarkerType): [number, number] {
    if (markerType === MarkerType.LocationCircle || markerType === MarkerType.LocationCross) {
        return [11, -8];
    } else if (markerType === MarkerType.LocationTriangle) {
        return [13, -10];
    } else {
        return [16, -40];
    }
}

export default class Map {

    static readonly coordinatePanelMapMarkerColor = '#445577';

    private openlayersMap: OlMap;
    private mapImageLayerGroup: LayerGroup;
    private openlayersVectorSource: VectorSource<any>;
    private openlayersVectorLayer: VectorLayer<any>;

    private markerSvgTextPromises: Record<any, Promise<string>> = {};
    private markerSvgText: string = null;
    private coloredMarkerImageCache: Record<string, [HTMLImageElement, Size]> = {};

    private hasMouseOverLocation_: boolean = false;

    private geolocation: Geolocation;
    private geolocationPromise: Promise<Geolocation>;
    private geolocatorUnsetter: (() => void)|null;

    private elevationProvider: ElevationProvider|null = null;
    private map3d: Map3d|null = null;
    private map3dControl;
    private defaultElevationMode: ElevationMode = Container.getDefaultElevationMode();

    private previousInterfaceMapMipName: string|null = null;
    private currentInterfaceMapMipName: string|null = null;

    private listeners: Record<string, ((data: Record<string, any>) => void)[]> = {
        'geolocate:start' : [],
        'geolocate:stop' : [],
        'geolocate:locate' : [],
    };

    constructor(readonly userInterface: UserInterface, private id: string) {

        this.openlayersVectorSource = new VectorSource({});

        this.openlayersVectorLayer = new VectorLayer({
            source: this.openlayersVectorSource,
            style: function(feature, resolution) {
                if (feature.get('custom_style')) {
                    return feature.get('custom_style')();
                }
                return null;
            },
        });

        this.mapImageLayerGroup = new LayerGroup();

        this.openlayersMap = new OlMap({
            layers: [
                this.mapImageLayerGroup,
                this.openlayersVectorLayer,
            ],
            view: new View({
                center: fromLonLat([5.0, 52.1]),
                zoom: 8,
                constrainResolution: true,
            }),
            target: id,
            controls: defaultControls({attribution: false}).extend([new Attribution({collapsible: false})]),
        });

        const locate = document.createElement('div');
        locate.className = 'ol-control ol-unselectable ol-locate-buttons';
        locate.innerHTML = '<button title="Stoppen" class="ol-locate-stop">&times;</button><button title="Mijn locatie" class="ol-locate-start"><div>&#9678;</div></button>';
        locate.querySelector('.ol-locate-start').addEventListener('click', () => {
            this.startGeolocating();
        });
        locate.querySelector('.ol-locate-stop').addEventListener('click', () => {
            this.stopGeolocating();
        });
        this.openlayersMap.addControl(
            new Control({
                element: locate,
            })
        );

        const source = document.createElement('div');
        source.className = 'ol-control ol-unselectable ol-source-buttons';
        source.innerHTML = '<button title="Kaart" class="ol-source"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-map" viewBox="0 0 16 16">\n' +
            '  <path fill-rule="evenodd" d="M15.817.113A.5.5 0 0 1 16 .5v14a.5.5 0 0 1-.402.49l-5 1a.5.5 0 0 1-.196 0L5.5 15.01l-4.902.98A.5.5 0 0 1 0 15.5v-14a.5.5 0 0 1 .402-.49l5-1a.5.5 0 0 1 .196 0L10.5.99l4.902-.98a.5.5 0 0 1 .415.103M10 1.91l-4-.8v12.98l4 .8zm1 12.98 4-.8V1.11l-4 .8zm-6-.8V1.11l-4 .8v12.98z"/>\n' +
            '</svg></button>';
        source.querySelector('.ol-source').addEventListener('click', (evt) => {
            if (evt.defaultPrevented) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();
            this.userInterface.openInterfaceMapSource();
        });
        source.querySelector('.ol-source').addEventListener('auxclick', (evt) => {
            if (evt.defaultPrevented) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();
            if (this.previousInterfaceMapMipName) {
                this.useMapImageProvider(this.previousInterfaceMapMipName);
            }
        });
        this.openlayersMap.addControl(
            new Control({
                element: source,
            })
        );

        const toggleMap3d = document.createElement('div');
        toggleMap3d.className = 'ol-control ol-unselectable ol-toggle-map3d-button';
        toggleMap3d.innerHTML = '<button title="3D inschakelen" class="ol-toggle-map3d"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-badge-3d" viewBox="0 0 16 16">' +
            '  <path d="M4.52 8.368h.664c.646 0 1.055.378 1.06.9.008.537-.427.919-1.086.919-.598-.004-1.037-.325-1.068-.756H3c.03.914.791 1.688 2.153 1.688 1.24 0 2.285-.66 2.272-1.798-.013-.953-.747-1.38-1.292-1.432v-.062c.44-.07 1.125-.527 1.108-1.375-.013-.906-.8-1.57-2.053-1.565-1.31.005-2.043.734-2.074 1.67h1.103c.022-.391.383-.751.936-.751.532 0 .928.33.928.813.004.479-.383.835-.928.835h-.632v.914zm3.606-3.367V11h2.189C12.125 11 13 9.893 13 7.985c0-1.894-.861-2.984-2.685-2.984zm1.187.967h.844c1.112 0 1.621.686 1.621 2.04 0 1.353-.505 2.02-1.621 2.02h-.844z"/>' +
            '  <path d="M14 3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM2 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/>' +
            '</svg></button>';

        toggleMap3d.querySelector('.ol-toggle-map3d').addEventListener('click', async (evt) => {
            if (evt.defaultPrevented) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();

            await this.showMap3d();
        });
        this.map3dControl = new Control({
            element: toggleMap3d,
        });
        if (this.defaultElevationMode !== ElevationMode.Disabled) {
            this.openlayersMap.addControl(this.map3dControl);
            document.body.classList.add('is-map-3d-available');
        }

        // Hack to allow cutout alt-key behaviour
        this.openlayersMap.getInteractions().forEach(function(interaction) {
            if (interaction instanceof DragPan) {
                // @ts-ignore
                const origCondition = interaction.condition_;
                // @ts-ignore
                interaction.condition_ = function(mapBrowserEvent: MapBrowserEvent) : boolean {
                    return origCondition(mapBrowserEvent) || altKeyOnly(mapBrowserEvent);
                }
            }
        });

        const mouseOverCutouts = [];
        let mouseOverRoute: Route = null;
        let mouseOverLocation: Location = null;
        let mouseOverShape: Shape = null;
        const mouseOverPixel = (pixel, forceChange = false, altKeyOnly = false) => {
            let cutout = null;
            if (
                !this.userInterface.isLocked()
                && (
                    (
                        this.userInterface.getRouteCollection().hasFocusedRoute()
                        || this.userInterface.getShapeCollection().hasFocusedShape()
                    ) === altKeyOnly
                )
            ) {
                cutout = this.cutoutsAtPixel(pixel)[0];
            }

            let changed = forceChange;
            if(cutout && mouseOverCutouts.indexOf(cutout) === -1) {
                cutout.mouseover(false);
                document.body.style.cursor = 'grab';
                mouseOverCutouts.push(cutout);
                changed = true;
            }

            for(let i = mouseOverCutouts.length - 1; i >= 0; i--) {
                const mouseOverCutout = mouseOverCutouts[i];
                if(mouseOverCutout !== cutout) {
                    mouseOverCutout.mouseout(false);
                    changed = true;
                    mouseOverCutouts.splice(i, 1);
                }
            }

            let newMouseOverLocationFeature = null;
            if (this.userInterface.getRouteCollection().getRouTechSelectedRoute()?.getEditMode() !== 'intermediate') {
                newMouseOverLocationFeature = this.openlayersMap.forEachFeatureAtPixel(pixel, function (feature, layer) {
                    if (feature && feature.get('location')) {
                        return feature;
                    }
                }, {
                    layerFilter: (layer) => {
                        if (this.userInterface.isLocked()) {
                            return false;
                        }

                        let mapLayer = this.userInterface.getLocationCollection().mapLayer;
                        mapLayer = isProxy(mapLayer) ? toRaw(mapLayer) : mapLayer;
                        return layer == mapLayer;
                    }
                });
            }

            if(mouseOverLocation && (!newMouseOverLocationFeature || mouseOverLocation !== newMouseOverLocationFeature.get('location'))) {
                mouseOverLocation.mouseout();
                mouseOverLocation = null;
                this.hasMouseOverLocation_ = false;
            }

            if(newMouseOverLocationFeature && mouseOverLocation !== newMouseOverLocationFeature.get('location')) {
                const location: Location = newMouseOverLocationFeature.get('location');
                location.mouseover();
                document.body.style.cursor = 'grab';
                mouseOverLocation = location;
                this.hasMouseOverLocation_ = true;
            }

            let newMouseOverRouteFeature = null;

            if (this.userInterface.routeCollection.getFocusedRoute()?.getDrawMode() !== 'cut') {
                newMouseOverRouteFeature = this.openlayersMap.forEachFeatureAtPixel(pixel, function (feature, layer) {
                    if (feature && feature.get('route')) {
                        return feature;
                    }
                }, {
                    layerFilter: (layer) => {
                        if (this.userInterface.isLocked()) {
                            return false;
                        }

                        let mainLayer = this.userInterface.getRouteCollection().mainLayer;
                        mainLayer = isProxy(mainLayer) ? toRaw(mainLayer) : mainLayer;
                        return layer == mainLayer;
                    }
                });
            }

            if(mouseOverRoute && (!newMouseOverRouteFeature || mouseOverRoute !== newMouseOverRouteFeature.get('route'))) {
                mouseOverRoute.mouseout();
                mouseOverRoute = null;
            }

            if(newMouseOverRouteFeature && mouseOverRoute !== newMouseOverRouteFeature.get('route')) {
                const route: Route = newMouseOverRouteFeature.get('route');
                route.mouseover();
                document.body.style.cursor = 'pointer';
                mouseOverRoute = route;
            }

            const newMouseOverShapeFeature = this.openlayersMap.forEachFeatureAtPixel(pixel, function (feature, layer) {
                if (feature && feature.get('shape')) {
                    return feature;
                }
            }, {
                layerFilter: (layer) => {
                    if (this.userInterface.isLocked()) {
                        return false;
                    }

                    let mainLayer = this.userInterface.getShapeCollection().mainLayer;
                    mainLayer = isProxy(mainLayer) ? toRaw(mainLayer) : mainLayer;
                    return layer == mainLayer;
                }
            });

            if(mouseOverShape && (!newMouseOverShapeFeature || mouseOverShape !== newMouseOverShapeFeature.get('shape'))) {
                mouseOverShape.mouseout();
                mouseOverShape = null;
            }

            if(newMouseOverShapeFeature && mouseOverShape !== newMouseOverShapeFeature.get('shape')) {
                const shape: Shape = newMouseOverShapeFeature.get('shape');
                shape.mouseover();
                document.body.style.cursor = 'pointer';
                mouseOverShape = shape;
            }

            if(mouseOverCutouts.length === 0 && mouseOverLocation === null && mouseOverRoute === null && mouseOverShape === null) {
                document.body.style.cursor = 'default';
            }

            if(changed) {
                this.openlayersVectorLayer.changed();
            }
        };

        this.openlayersMap.on('pointermove', (e) => {
            if(e.dragging) {
                return;
            }

            mouseOverPixel(e.pixel, false, altKeyOnly(e));
        });

        const ctrlClickCutoutStatus = {
            pixel: null,
            zIndex: null,
            reset: false,
        };
        this.openlayersMap.on('singleclick', (e) => {
            if (platformModifierKeyOnly(e)) {
                // ctrl+click on cutout = change cutout zIndex ordering

                if (
                    ctrlClickCutoutStatus.pixel === null
                    || (e.pixel[0] !== ctrlClickCutoutStatus.pixel[0] && e.pixel[1] !== ctrlClickCutoutStatus.pixel[1])
                ) {
                    ctrlClickCutoutStatus.reset = true;
                }

                const cutoutsAtPixel = this.cutoutsAtPixel(e.pixel);

                if (cutoutsAtPixel.length === 0) {
                    return;
                }

                e.stopPropagation();

                let cutout = null;
                if (cutoutsAtPixel.length === 1) {
                    cutout = cutoutsAtPixel[0];
                } else {
                    if (ctrlClickCutoutStatus.reset) {
                        ctrlClickCutoutStatus.pixel = e.pixel;
                        ctrlClickCutoutStatus.zIndex = cutoutsAtPixel.shift().getZIndex();
                        ctrlClickCutoutStatus.reset = false;
                    }

                    for (const cutoutAtPixel of cutoutsAtPixel) {
                        if (cutoutAtPixel.getZIndex() < ctrlClickCutoutStatus.zIndex) {
                            cutout = cutoutAtPixel;
                            break;
                        }
                    }

                }

                if (!cutout || cutout === cutoutsAtPixel[cutoutsAtPixel.length - 1]) {
                    ctrlClickCutoutStatus.reset = true;

                    if (!cutout) {
                        return;
                    }
                }

                cutout.moveToFront();

                setTimeout(() => {
                    mouseOverPixel(e.pixel, cutoutsAtPixel.length === 1);
                });
            }
        });

        this.openlayersMap.getViewport().addEventListener('contextmenu', (evt) => {
            evt.preventDefault();

            if (document.querySelector('.ol-source').contains(evt.target)) {
                return;
            }

            const feature = this.openlayersMap.forEachFeatureAtPixel(
                this.openlayersMap.getEventPixel(evt),
                function (feature, layer) {
                    if (feature && feature.get('cutout')) {
                        return feature;
                    }
                }
            );
            if (feature && feature.get('cutout')) {
                feature.get('cutout').openWorkspaceDropdownMenu(evt);
            } else if (!this.userInterface.isLocked()) {
                this.userInterface.openGeneralDropdownMenu(evt);
            }
        });
    }

    async buildMipLayersForOpenLayers(mip: MapImageProvider): Promise<Layer[]> {
        const cache = await Container.getCache();

        const tileLoader = new OlTileLoader(cache);
        tileLoader.setUseFallback(true);

        const layers: Layer[] = await mip.buildOlLayers();

        for (const layer of layers) {
            const source = layer.getSource();
            source.setTileLoadFunction(tileLoader.getTileLoadFunction());
            source.setAttributions(mip.workspace_attributions);
        }

        return layers;
    }

    async useMapImageProvider(name: string) {
        if (name === this.currentInterfaceMapMipName) {
            return;
        }

        const mip = Container.mapImageProvider(name);
        const layers = await this.buildMipLayersForOpenLayers(mip);

        this.mapImageLayerGroup.setLayers(new olCollection(layers));

        this.previousInterfaceMapMipName = this.currentInterfaceMapMipName
        this.currentInterfaceMapMipName = name;
        this.userInterface.reactiveData.mapOptions.currentInterfaceMapMipName = mip.name; // TODO: do not reference reactiveData directly

        const wgsSystem = CoordinateConverter.getCoordinateSystem('EPSG:4326');
        const boundingPolygon = await mip.getBoundingPolygon(wgsSystem);
        let boundsExtent = undefined;
        if (boundingPolygon !== null) {
            const boundLatLngs = boundingPolygon.map((coordinate: OLConvertibleCoordinate) => coordinate.toOpenLayersCoordinate());
            boundsExtent = new Polygon([boundLatLngs]).getExtent();
        }

        const oldView = this.openlayersMap.getView();
        const newCenter = boundsExtent && !containsCoordinate(boundsExtent, oldView.getCenter()) ? getCenter(boundsExtent) : oldView.getCenter();
        const newView = new View({
            center: newCenter,
            zoom: oldView.getZoom(),
            constrainResolution: oldView.getConstrainResolution(),
            extent: boundsExtent,
            minZoom: Math.max(mip.workspace_zoom_range[0] + (isMobile() ? -1 : 0), 0),
            constrainOnlyCenter: true,
        })
        this.openlayersMap.setView(newView);

        this.userInterface.getRouteCollection().setWorkspaceRouteConfig(mip.getWorkspaceRouteConfig());
        this.userInterface.getShapeCollection().setWorkspaceShapeConfig(mip.getWorkspaceRouteConfig());
        newView.on('change:resolution', debounce((event) => {
            this.userInterface.getRouteCollection().workspaceZoomed();
            this.userInterface.getShapeCollection().workspaceZoomed();
            // console.log(this.openlayersMap.getView().getZoom());
        }, 50));

        // ol-cesium sometimes crashes on source/layer changes - rebuild next time we switch to 3d
        this.map3d?.destroy();
    }

    getOpenlayersVectorSource(): VectorSource<any> {
        return this.openlayersVectorSource;
    }

    getOpenlayersVectorLayer(): VectorLayer<any> {
        return this.openlayersVectorLayer;
    }
    getOpenlayersMap(): OlMap {
        return this.openlayersMap;
    }
    getElevationProvider(): ElevationProvider|null {
        return this.elevationProvider;
    }

    async ensureElevationProvider(force_optin: boolean = false): Promise<ElevationProvider|null> {
        if (this.defaultElevationMode === ElevationMode.Disabled) {
            return null;
        }

        const initialize = this.elevationProvider === null;
        if (initialize) {
            if (this.defaultElevationMode === ElevationMode.OptIn && !force_optin) {
                return null;
            }

            this.elevationProvider = new ElevationProvider(this);
        }
        await this.elevationProvider.awaitInitialization();

        if (initialize) {
            this.userInterface.getRouteCollection().updateAllElevationData();
        }

        return this.elevationProvider;
    }

    updateElevationMode() {
        const hasControl = this.openlayersMap.getControls().getArray().indexOf(this.map3dControl) >= 0;
        this.defaultElevationMode = Container.getDefaultElevationMode();
        this.userInterface.reactiveData.defaultElevationModeDisabled = this.defaultElevationMode === ElevationMode.Disabled;
        if (this.defaultElevationMode !== ElevationMode.Disabled) {
            if (!hasControl) {
                this.openlayersMap.addControl(this.map3dControl);
                document.body.classList.add('is-map-3d-available');

                this.userInterface.getRouteCollection().updateAllElevationData();
            }
        } else {
            if (hasControl) {
                this.openlayersMap.removeControl(this.map3dControl);
                document.body.classList.remove('is-map-3d-available');

                this.userInterface.getRouteCollection().updateAllElevationData();
            }
        }
    }

    async showMap3d() {
        if (this.map3d === null) {
            const elevationProvider = await this.ensureElevationProvider(true);
            if (elevationProvider === null) {
                throw Error('Caught unexpected empty elevationProvider');
            }

            this.map3d = new Map3d(this, elevationProvider, 'map-3d-canvas');
        }

        await this.map3d.show();
    }

    hasMouseOverLocation(): boolean {
        return this.hasMouseOverLocation_;
    }

    getCenter(): WGS84 {
        return (new WGS84System()).fromOpenLayersCoordinate(this.openlayersMap.getView().getCenter());
    }

    getBoundingPolygon(): WGS84[] {
        const extend = this.openlayersMap.getView().calculateExtent(this.openlayersMap.getSize());

        const wgs84System = new WGS84System();

        return <WGS84[]>[
            wgs84System.fromOpenLayersCoordinate(olExtent.getBottomLeft(extend)),
            wgs84System.fromOpenLayersCoordinate(olExtent.getBottomRight(extend)),
            wgs84System.fromOpenLayersCoordinate(olExtent.getTopRight(extend)),
            wgs84System.fromOpenLayersCoordinate(olExtent.getTopLeft(extend)),
        ];
    }

    listen(event: string, callback: (data: Record<string, any>) => void) {
        if (typeof this.listeners[event] === 'undefined') {
            throw Error('Invalid event name ' + event);
        }

        this.listeners[event].push(callback);
    }

    unlisten(event: string, callback: (data: Record<string, any>) => void) {
        if (typeof this.listeners[event] === 'undefined') {
            throw Error('Invalid event name ' + event);
        }

        const index = this.listeners[event].indexOf(callback);

        if (index === -1) {
            throw Error('The given callback is not listening to ' + event);
        }

        this.listeners.splice(index, 1);
    }

    private trigger(event: string, data: Record<string, any>) {
        for (const listener of this.listeners[event]) {
            listener(data);
        }
    }

    fitTo(
        cutouts: Cutout<Coordinate & OLConvertibleCoordinate, any, any>[],
        routes: Route[],
        locations: Location[],
        shapes: Shape[],
    ): void {
        routes = routes.filter(route => route.getGeometry().getCoordinates().length > 0);
        shapes = shapes.filter(shape => shape.getGeometry().getCoordinates().length > 0);

        if(cutouts.length === 0 && routes.length === 0 && locations.length === 0 && shapes.length === 0) {
            return;
        }

        let extent = olExtent.createEmpty();

        if (cutouts.length > 0) {
            let minLat = null, maxLat = null, minLng = null, maxLng = null;

            for(const cutout of cutouts) {
                for(const wsCoordinate of cutout.mapPolygonWorkspace) {
                    if(minLat === null || wsCoordinate.getY() < minLat) {
                        minLat = wsCoordinate.getY();
                    }
                    if(maxLat === null || wsCoordinate.getY() > maxLat) {
                        maxLat = wsCoordinate.getY();
                    }

                    if(minLng === null || wsCoordinate.getX() < minLng) {
                        minLng = wsCoordinate.getX();
                    }
                    if(maxLng === null || wsCoordinate.getX() > maxLng) {
                        maxLng = wsCoordinate.getX();
                    }
                }
            }

            const lowerLeft = fromLonLat([minLng, minLat]);
            const upperRight = fromLonLat([maxLng, maxLat]);

            olExtent.extend(extent, [lowerLeft[0], lowerLeft[1], upperRight[0], upperRight[1]]);
        }

        for (const route of routes) {
            olExtent.extend(extent, route.getGeometry().getExtent());
        }

        for (const location of locations) {
            olExtent.extend(extent, location.getGeometry().getExtent());
        }

        for (const shape of shapes) {
            olExtent.extend(extent, shape.getGeometry().getExtent());
        }

        this.openlayersMap.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            maxZoom: 20,
        });
    }

    fitToCoordinate(coordinate: Coordinate): void {
        const olConvCoord = <OLConvertibleCoordinate>CoordinateConverter
            .convert(coordinate, CoordinateConverter.getCoordinateSystem('EPSG:4326'));

        const extent = olExtent.createEmpty();

        olExtent.extendCoordinate(extent, olConvCoord.toOpenLayersCoordinate());

        this.openlayersMap.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            maxZoom: 20,
        });
    }

    fitToExtent(extent: Extent): void {
        this.openlayersMap.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            maxZoom: 20,
        });
    }

    /**
     * Call callback for each cutout under pixel, in descending order of cutout.zIndex. callback will
     * be called until it returns a truthy value for a cutout, in which case the function will return
     * this value. In other cases, it returns undefined
     * @param pixel
     * @param callback
     */
    forEachCutoutAtPixel<T>(pixel, callback: (cutout: Cutout<any, any, any>, feature: Feature<any>, layer: Layer<any>) => T): (T|unknown) {
        const features = [];

        this.openlayersMap.forEachFeatureAtPixel(pixel, function (feature, layer) {
            if (feature && feature.get('cutout')) {
                features.push([feature, layer]);
            }
        }, {
            layerFilter: (layer) => {
                return layer == this.openlayersVectorLayer;
            }
        });

        features.sort(([featureA, layerA], [featureB, layerB]) => featureB.get('cutout').zIndex - featureA.get('cutout').zIndex);

        for (const featureArray of features) {
            let result = callback(featureArray[0].get('cutout'), featureArray[0], featureArray[1]);
            if (result) {
                return result;
            }
        }
    }

    cutoutsAtPixel(pixel): Cutout<any, any, any>[] {
        const cutouts: Cutout<any, any, any>[] = [];
        this.forEachCutoutAtPixel(pixel, function (cutout) {
            cutouts.push(cutout);
        });
        return cutouts;
    }

    geolocate(): Promise<Geolocation> {
        // https://openlayers.org/en/latest/examples/geolocation.html
        // https://openlayers.org/workshop/en/mobile/geolocation.html

        if (!this.geolocation) {
            this.geolocation = new Geolocation({
                // enableHighAccuracy must be set to true to have the heading value.
                trackingOptions: {
                    enableHighAccuracy: true,
                },
                tracking: true,
                projection: this.openlayersMap.getView().getProjection(),
            });

            this.geolocationPromise = new Promise((resolve, reject) => {
                let handled = false;
                this.geolocation.once('change:position', () => {
                    if (handled) {
                        return;
                    }

                    handled = true;
                    resolve(this.geolocation);
                });

                this.geolocation.on('error', (error) => {
                    if (handled) {
                        return;
                    }

                    let additionalInfo = '';
                    if (isIOS()) {
                        additionalInfo = ' - Als je een iPhone gebruikt, zorg er dan voor dat Safari locatie gegevens mag gebruiken onder Instellingen > Privacy > Safari'
                    }

                    handled = true;
                    alert(error.message + additionalInfo);
                    this.geolocationPromise = null;
                    this.geolocation = null;
                    reject(error);
                });
            });
        }

        return this.geolocationPromise;
    }

    startGeolocating(): void {
        let loaded = false;
        setTimeout(() => {
            if (!loaded) {
                document.querySelector('.ol-locate-start > div').classList.add('animate-size');
            }
        }, 100);

        const loadedFn = () => {
            loaded = true;
            document.querySelector('.ol-locate-start > div').classList.remove('animate-size');
        };

        this.geolocate().then((geolocation) => {
            loadedFn();

            this.trigger('geolocate:start', {lonlat: toLonLat(geolocation.getPosition())});

            if (!this.geolocatorUnsetter) {
                let geomCallback, posCallback;

                const accuracyFeature = new Feature();
                geolocation.on('change:accuracyGeometry', geomCallback = () => {
                    accuracyFeature.setGeometry(geolocation.getAccuracyGeometry());
                });
                accuracyFeature.setStyle(new Style({
                    image: new Circle({
                        fill: new Fill({
                            color: 'rgba(255,255,255,0.4)',
                        }),
                        stroke: new Stroke({
                            color: '#3399CC',
                            width: 1.25,
                        }),
                        radius: 5,
                    }),
                    fill: new Fill({
                        color: 'rgba(255,255,255,0.4)',
                    }),
                    stroke: new Stroke({
                        color: 'rgb(51,153,204,0.6)',
                        width: 1.25,
                    }),
                }));

                const positionFeature = new Feature();
                positionFeature.setStyle(
                    new Style({
                        image: new CircleStyle({
                            radius: 6,
                            fill: new Fill({
                                color: '#3399CC',
                            }),
                            stroke: new Stroke({
                                color: '#fff',
                                width: 3,
                            }),
                        }),
                    })
                );

                geolocation.on('change:position', posCallback = () => {
                    const coordinates = geolocation.getPosition();
                    positionFeature.setGeometry(coordinates ? new Point(coordinates) : null);

                    this.trigger('geolocate:locate', {lonlat: toLonLat(coordinates)});
                });

                geomCallback();
                posCallback();

                this.openlayersVectorSource.addFeature(accuracyFeature);
                this.openlayersVectorSource.addFeature(positionFeature);
                document.querySelector('.ol-locate-buttons').classList.add('ol-locate-active');

                this.geolocatorUnsetter = () => {
                    geolocation.un('change:accuracyGeometry', geomCallback);
                    geolocation.un('change:position', posCallback);
                    this.openlayersVectorSource.removeFeature(accuracyFeature);
                    this.openlayersVectorSource.removeFeature(positionFeature);
                    document.querySelector('.ol-locate-buttons').classList.remove('ol-locate-active');

                    this.trigger('geolocate:stop', {});
                };
            }

            this.openlayersMap.getView().fit(geolocation.getAccuracyGeometry(), {
                maxZoom: 18,
                duration: 500,
            });
        }).catch(loadedFn);
    }

    stopGeolocating(): void {
        if (this.geolocatorUnsetter) {
            this.geolocatorUnsetter();
            this.geolocatorUnsetter = null;
        }
    }

    markerDownloaded() {
        return Object.keys(this.coloredMarkerImageCache).length > 0;
    }

    private getMarkerSvgTextPromise(markerType: MarkerType): Promise<string> {
        if (typeof this.markerSvgTextPromises[markerType] === 'undefined') {
            this.markerSvgTextPromises[markerType] = new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('GET', getMarkerImage(markerType), true);
                xhr.responseType = 'blob';
                xhr.onload = (e) => {
                    const blob = xhr.response;

                    const fr = new FileReader();
                    fr.onload = (e) => {
                        // @ts-ignore
                        this.markerSvgText = fr.result;
                        // @ts-ignore
                        resolve(fr.result);
                    };
                    fr.readAsText(blob);
                };
                xhr.send(null);
            });
        }

        return this.markerSvgTextPromises[markerType];
    }

    getImmediateColoredMarkerImage(markerType: MarkerType, color: string = null, highlight: boolean = false): [HTMLImageElement, Size] {
        const cacheKey = ['markerType_' + markerType, color === null ? 'null' : 'color_' + color, highlight ? 'hl_true' : 'hl_false'].join('_');
        if (this.coloredMarkerImageCache[cacheKey] !== undefined) {
            return this.coloredMarkerImageCache[cacheKey];
        }

        throw new UserError('Er is iets misgegaan bij intekenen van punten');
    }

    getColoredMarkerImage(markerType: MarkerType, color: string = null, highlight: boolean = false, removeHighlight: boolean = false): Promise<[HTMLImageElement, Size]> {
        const cacheKey = ['markerType_' + markerType, color === null ? 'null' : 'color_' + color, highlight ? 'hl_true' : 'hl_false'].join('_');
        if (this.coloredMarkerImageCache[cacheKey] !== undefined) {
            return Promise.resolve(this.coloredMarkerImageCache[cacheKey]);
        }

        return this.mustUseAlternativeSvgDraw().then((mustUseCanvg: boolean) => {
            return this.retrieveColoredMarkerImage(markerType, color, highlight, mustUseCanvg, removeHighlight).then(([img, markerSize]) => {
                return this.coloredMarkerImageCache[cacheKey] = [img, markerSize];
            });
        });
    }

    private retrieveColoredMarkerImage(markerType: MarkerType, color: string|null, highlight: boolean, useCanvg: boolean, removeHighlight: boolean = false) : Promise<[HTMLImageElement, Size]>
    {
        return this.getMarkerSvgTextPromise(markerType).then((svgText) => {
            return new Promise<[HTMLImageElement, Size]>((resolve, reject) => {
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');

                const svgTag = svgDoc.documentElement;
                const markerSize = [parseFloat(svgTag.getAttribute('width')), parseFloat(svgTag.getAttribute('height'))];

                if (markerType === MarkerType.LocationCircle) {
                    const mainCircle = svgDoc.getElementById('circle854');
                    const highlightCircle = svgDoc.getElementById('circle844');

                    if (color !== null) {
                        mainCircle.style.stroke = color;
                        highlightCircle.style.stroke = color;
                    }

                    if (highlight) {
                        highlightCircle.style.strokeWidth = (0.6 * parseFloat(mainCircle.style.strokeWidth)) + 'px';
                        highlightCircle.style.stroke = '#ffffff';
                        highlightCircle.style.strokeOpacity = '0.5';
                        mainCircle.style.strokeWidth = (2 * parseFloat(mainCircle.style.strokeWidth)) + 'px';
                    }
                } else if (markerType === MarkerType.LocationCross) {
                    const cross = svgDoc.getElementById('rect850');
                    const innerCross = svgDoc.getElementById('rect842');

                    if (color !== null) {
                        cross.style.fill = color;
                        innerCross.style.fill = color;
                    }

                    if (highlight) {
                        cross.style.stroke = color;
                        cross.style.strokeWidth = '2px';

                        innerCross.style.display = 'inline';
                        svgDoc.getElementById('path925').style.display = 'inline';
                        innerCross.style.fill = '#ffffff';
                        innerCross.style.fillOpacity = '0.4';
                    }

                } else if (markerType === MarkerType.LocationTriangle) {
                    const main = svgDoc.getElementById('path848');
                    const highlightLarge = svgDoc.getElementById('path872');
                    const highlightInner = svgDoc.getElementById('path876');

                    if (color !== null) {
                        main.style.fill = color;
                    }

                    if (highlight) {
                        highlightLarge.style.display = 'inline';
                        highlightInner.style.display = 'inline';

                        highlightLarge.style.fill = color;

                        highlightInner.style.fill = '#ffffff';
                        highlightInner.style.fillOpacity = '0.4';

                        svgDoc.getElementById('path875').style.display = 'inline';
                        svgDoc.getElementById('path867').style.display = 'inline';
                    }

                } else {
                    if (color !== null) {
                        const path = svgDoc.getElementById('path1424');
                        path.style.fill = color;
                    }

                    if (highlight) {
                        const circle = svgDoc.getElementById('circle889');
                        circle.style.fill = '#999999';
                    } else if (removeHighlight) {
                        for (const element of ['circle889', 'circle847', 'path2035', 'circle899']) {
                            const circle = svgDoc.getElementById(element);
                            circle.parentNode.removeChild(circle);
                        }
                    }
                }

                const newSvgText = new XMLSerializer().serializeToString(svgDoc);

                if (useCanvg) {
                    this.makeMarkerHtmlImageElementUsingCanvg(newSvgText, markerSize).then((img) => {
                        resolve([img, markerSize]);
                    });
                } else {
                    const blob = new Blob([newSvgText], {type: 'image/svg+xml'});
                    const objectUrl = URL.createObjectURL(blob);
                    let img = document.createElement('img');
                    img.src = objectUrl;
                    img.addEventListener('load', () => {
                        URL.revokeObjectURL(objectUrl);
                        resolve([img, markerSize]);
                    }, {once: true});
                }
            });
        });
    }

    private mustUseAlternativeSvgDraw_: boolean = null;
    private mustUseAlternativeSvgDraw(): Promise<boolean> {
        if (this.mustUseAlternativeSvgDraw_ !== null) {
            return Promise.resolve(this.mustUseAlternativeSvgDraw_);
        }

        return this.retrieveColoredMarkerImage(MarkerType.MapMarker, '#ff0000', false, false).then(([img, markerSize]) => {
            return new Promise<boolean>((resolve, reject) => {
                const canvas = document.createElement('canvas');
                canvas.width = markerSize[0];
                canvas.height = markerSize[1];
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                const data = ctx.getImageData(Math.floor(markerSize[0]/2), Math.floor(markerSize[1]/2), 1, 1).data;
                const [r, g, b] = [data[0], data[1], data[2]];

                // If r === 0 in the center of our #ff0000 red image, the color has not been correctly drawn
                // (on e.g. old Android phones). Therefore, when then return true to use the alternative method
                resolve(this.mustUseAlternativeSvgDraw_ = (r === 0));
            });
        })
    }

    private makeMarkerHtmlImageElementUsingCanvg(svgText: string, markerSize: Size): Promise<HTMLImageElement> {
        return new Promise<HTMLImageElement>((resolve, reject) => {
            const canvas = document.createElement('canvas');
            canvas.width = markerSize[0];
            canvas.height = markerSize[1];
            const ctx = canvas.getContext('2d');

            import('canvg').then(({Canvg: Canvg}) => {
                const v = Canvg.fromString(ctx, svgText);
                v.start();
                v.ready().then(() => {
                    const objectUrl = canvas.toDataURL('image/png');

                    v.stop();

                    let img = document.createElement('img');
                    img.src = objectUrl;
                    img.addEventListener('load', () => {
                        resolve(img);
                    }, {once: true});
                });
            });
        });
    }

    getMapMarker(markerType: MarkerType, color: string = null, highlight: boolean = false) {
        return this.getColoredMarkerImage(markerType, color, highlight).then(([img, markerSize]) => {
            let anchor;
            if (markerType === MarkerType.LocationCircle || markerType === MarkerType.LocationCross) {
                anchor = [markerSize[0] / 2, markerSize[1] / 2];
            } else {
                anchor = [markerSize[0] / 2, markerSize[1]];
            }

            return new Icon({
                anchor: anchor,
                anchorXUnits: 'pixels',
                anchorYUnits: 'pixels',
                scale : 1,
                opacity: 1,
                img: img,
            });
        });
    }

    getCoordinatePanelMarker() {
        return this.getColoredMarkerImage(MarkerType.MapMarker, Map.coordinatePanelMapMarkerColor, null, true)
            .then(([img, markerSize]) => {
                return new Icon({
                    anchor: [markerSize[0] / 2, markerSize[1]],
                    anchorXUnits: 'pixels',
                    anchorYUnits: 'pixels',
                    scale : 0.75,
                    opacity: 1,
                    img: img,
                });
            });
    }
}
